const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export interface ApiFetchOptions {
  /** Absolute URL of POST /auth/refresh. */
  refreshUrl: string;
  baseFetch?: (request: Request) => Promise<Response>;
  getCsrf?: () => string | undefined;
  /** Called once when a 401 could not be recovered by refreshing. */
  onSessionExpired?: () => void;
  onRateLimited?: (info: { code: string; retryAfter?: number }) => void;
}

export type ApiFetch = ((request: Request) => Promise<Response>) & {
  /** Single-flight refresh: concurrent callers share one POST /auth/refresh. Resolves true on success. */
  refresh: () => Promise<boolean>;
};

async function errorCode(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.clone().json()) as { error?: { code?: string } };
    return body.error?.code;
  } catch {
    return undefined;
  }
}

function isAuthPath(request: Request): boolean {
  return new URL(request.url).pathname.includes("/auth/");
}

/**
 * fetch wrapper used by the openapi-fetch client: CSRF header on unsafe methods, one shared refresh
 * on UNAUTHENTICATED followed by a replay, and rate-limit notification. Never refreshes on /auth/*.
 */
export function createApiFetch(options: ApiFetchOptions): ApiFetch {
  const base = options.baseFetch ?? ((request: Request) => fetch(request));
  const getCsrf = options.getCsrf ?? (() => undefined);
  let inflight: Promise<boolean> | null = null;

  function withCsrf(request: Request): Request {
    const token = getCsrf();
    // Login is exempt on the server; sending the header there is harmless and absent anyway.
    if (!SAFE_METHODS.has(request.method) && token) request.headers.set("X-CSRF-Token", token);
    return request;
  }

  function refresh(): Promise<boolean> {
    inflight ??= (async () => {
      try {
        const headers = new Headers();
        const token = getCsrf();
        if (token) headers.set("X-CSRF-Token", token);
        const response = await base(
          new Request(options.refreshUrl, { method: "POST", credentials: "include", headers }),
        );
        return response.ok;
      } catch {
        return false;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  async function apiFetch(request: Request): Promise<Response> {
    const replay = isAuthPath(request) ? null : request.clone();
    let response = await base(withCsrf(request));

    if (replay && response.status === 401 && (await errorCode(response)) === "UNAUTHENTICATED") {
      if (await refresh()) response = await base(withCsrf(replay));
      // Refresh failed, or the replay is still rejected: the session is gone.
      if (response.status === 401) options.onSessionExpired?.();
    }

    if (response.status === 429) {
      const retry = Number(response.headers.get("Retry-After"));
      options.onRateLimited?.({
        code: (await errorCode(response)) ?? "RATE_LIMITED",
        retryAfter: Number.isFinite(retry) && retry > 0 ? retry : undefined,
      });
    }
    return response;
  }

  return Object.assign(apiFetch, { refresh });
}
